/**
 * DashboardPage.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Top N Dashboard – Nawara BI
 *
 * Fonctionnalités :
 *  • Sélection de période  (De / À)
 *  • Sélection Top N       (5 | 10 | 20 | 50)
 *  • 6 classements MDX via SSAS :
 *      1. Top Vendeurs      (Vendeur.dim)
 *      2. Top Marques       (Categorie.dim)
 *      3. Top Magasins      (Depot.dim)
 *      4. Top Familles      (Famille.dim)
 *      5. Top Saisons       (Saison.dim)
 *      6. Top Sous-Familles (Sousfamille.dim)
 *      7. Top Couleurs      (Couleur.dim)
 *      8. Top Tailles       (Taille.dim)
 *      9. Top Ventes/Article (ArticleParent.dim)
 *     10. Top Groupes       (Groupe.dim)
 *     11. Top Univers       (Univers.dim)
 *     12. Ventes par Date   (Date.dim) — avec filtre granularité jour/semaine/mois/année
 *
 * ── CONFIGURATION ────────────────────────────────────────────────────────────
 * Adaptez les constantes MDX_* ci-dessous à votre cube SSAS.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { executeQuery, getCubeMetadata } from "../features/cube/api";
import { useUserScope } from "../features/auth/useUserScope";
import { useExportExcel } from "../features/dashboard/export/useExportExcel";
import { useExportPDF } from "../features/dashboard/export/useExportPDF";
import { colors } from "../shared/ui/tokens";
import { CUBE_DIMENSIONS, CUBE_MEASURES, mdxHierarchy, resolveSelection } from "../features/cube/dimensions";

// Mesures de VENTE (les [Pttc]/[Qte] sans suffixe sont celles des achats)
const SALES_MEASURES = [CUBE_MEASURES.caTtc, CUBE_MEASURES.qte];
/** Index des colonnes CA / Qté dans la réponse (en-têtes = libellés des mesures). */
const salesColumnIndexes = (columns: string[]) => ({
  caIdx: columns.indexOf(CUBE_MEASURES.caTtc),
  qteIdx: columns.indexOf(CUBE_MEASURES.qte),
});

const CUBE_NAME = "Maxi-cube"; // Nom du cube
const TOP_N_MARGIN = 5; // lignes en plus demandées au Top N (voir fetchTopN)

// ✅ Mesures hardcodées — l'API /cube/measures ne retourne pas Pttc
// mais elle existe bien dans le cube (utilisée par CouleurView, VendeurView, etc.)
const MEASURE_CA = `[Measures].[${CUBE_MEASURES.caTtc}]`;
const MEASURE_QTE = `[Measures].[${CUBE_MEASURES.qte}]`;

// ── FORMAT [Dimension].[Hierarchy] ───────────────────────────────────────────
// These paths mirror the cube metadata. The actual request values are resolved
// from GET /dimensions immediately before a TopN query is built.
const DIM_VENDEUR = mdxHierarchy(CUBE_DIMENSIONS.vendeur);
const DIM_MARQUE = mdxHierarchy(CUBE_DIMENSIONS.marque);
const DIM_MAGASIN = mdxHierarchy(CUBE_DIMENSIONS.depot);
const DIM_FAMILLE = mdxHierarchy(CUBE_DIMENSIONS.famille);
const DIM_SAISON = mdxHierarchy(CUBE_DIMENSIONS.saison);
const DIM_SFAMILLE = mdxHierarchy(CUBE_DIMENSIONS.sousfamille);
const DIM_COULEUR = mdxHierarchy(CUBE_DIMENSIONS.couleur);
const DIM_TAILLE = mdxHierarchy(CUBE_DIMENSIONS.taille);
const DIM_ARTICLE_PARENT = mdxHierarchy(CUBE_DIMENSIONS.articleParent);
const DIM_GROUPE = mdxHierarchy(CUBE_DIMENSIONS.groupe);
const DIM_UNIVERS = mdxHierarchy(CUBE_DIMENSIONS.univers);

const DIM_DATE = "[Date].[Date]"; // Dimension Date pour l'affichage temporel

// ── Granularité du panel Date ─────────────────────────────────────────────────
type DateGranularity = "day" | "week" | "month" | "year";

// ── Brand (AVDASH white theme) ───────────────────────────────────────────────
const NW = {
  bgDeep: colors.neutral[0],
  bgMid: colors.neutral[100],
  bgLight: colors.brand[600],
  card: "rgba(255,255,255,0.95)",
  cardBorder: "rgba(29,91,168,0.18)",
  mauve: colors.brand[600],
  mauveGlow: colors.accent[300],
  mauveDim: "rgba(29,91,168,0.12)",
  mauveBorder: "rgba(29,91,168,0.25)",
  white: colors.neutral[900],
  dimWhite: "rgba(24,75,140,0.65)",
  faint: "rgba(29,91,168,0.07)",
  gold: colors.warning[500],
  silver: colors.neutral[500],
  bronze: colors.warning[700],
};

// ── Chart Types ───────────────────────────────────────────────────────────────
type ChartType = "bar" | "line" | "area" | "pie" | "radar" | "table";

const CHART_TYPES: { type: ChartType; emoji: string; label: string }[] = [
  { type: "table", emoji: "📋", label: "Tableau" },
  { type: "bar", emoji: "📊", label: "Barres" },
  { type: "line", emoji: "📈", label: "Lignes" },
  { type: "area", emoji: "🏔️", label: "Aire" },
  { type: "pie", emoji: "🟠", label: "Camembert" },
  { type: "radar", emoji: "❄️", label: "Radar" },
];

// Pie chart colors
const PIE_COLORS = [
  "#2B5CE6",
  "#3ECFBF",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#06B6D4",
  "#F97316",
  "#10B981",
  "#2B5CE699",
  "#3ECFBF99",
];

// ── Types ────────────────────────────────────────────────────────────────────
interface RankRow {
  member: string;
  ca: number;
  qte?: number;
}

/** Critère de classement du Top N. */
type RankMetric = "ca" | "qte";
const METRIC_LABEL: Record<RankMetric, string> = { ca: "CA", qte: "Quantité" };
const metricValue = (r: RankRow, m: RankMetric): number => (m === "qte" ? r.qte ?? 0 : r.ca);

interface Panel {
  id: string;
  title: string;
  icon: string;
  dim: string; // kept for display/legacy
  dimensionName: string;
  hierarchyName: string;
  levelName: string;
  color: string;
}

const PANELS: Panel[] = [
  {
    id: "vendeur",
    title: "Top Vendeurs",
    icon: "👤",
    dim: DIM_VENDEUR,
    ...CUBE_DIMENSIONS.vendeur,
    color: "#7C3AED",
  },
  {
    id: "marque",
    title: "Top Marques",
    icon: "🏷️",
    dim: DIM_MARQUE,
    ...CUBE_DIMENSIONS.marque,
    color: "#DB2777",
  },
  {
    id: "magasin",
    title: "Top Magasins",
    icon: "🏪",
    dim: DIM_MAGASIN,
    ...CUBE_DIMENSIONS.depot,
    color: "#059669",
  },
  {
    id: "famille",
    title: "Top Familles",
    icon: "📂",
    dim: DIM_FAMILLE,
    ...CUBE_DIMENSIONS.famille,
    color: "#2563EB",
  },
  {
    id: "saison",
    title: "Top Saisons",
    icon: "🌸",
    dim: DIM_SAISON,
    ...CUBE_DIMENSIONS.saison,
    color: "#D97706",
  },
  {
    id: "sfamille",
    title: "Top Sous-Familles",
    icon: "📁",
    dim: DIM_SFAMILLE,
    ...CUBE_DIMENSIONS.sousfamille,
    color: "#DC2626",
  },
  {
    id: "couleur",
    title: "Top Couleurs",
    icon: "🎨",
    dim: DIM_COULEUR,
    ...CUBE_DIMENSIONS.couleur,
    color: "#0891B2",
  },
  {
    id: "taille",
    title: "Top Tailles",
    icon: "📐",
    dim: DIM_TAILLE,
    ...CUBE_DIMENSIONS.taille,
    color: "#7C3AED",
  },
  {
    id: "articleparent",
    title: "Top Ventes par Article",
    icon: "🛍️",
    dim: DIM_ARTICLE_PARENT,
    ...CUBE_DIMENSIONS.articleParent,
    color: "#EA580C",
  },
  {
    id: "groupe",
    title: "Top Groupes",
    icon: "🗂️",
    dim: DIM_GROUPE,
    ...CUBE_DIMENSIONS.groupe,
    color: "#4F46E5",
  },
  {
    id: "univers",
    title: "Top Univers",
    icon: "🌐",
    dim: DIM_UNIVERS,
    ...CUBE_DIMENSIONS.univers,
    color: "#BE185D",
  },
  {
    id: "date",
    title: "Ventes par Date",
    icon: "📅",
    dim: DIM_DATE,
    ...CUBE_DIMENSIONS.date,
    color: "#0D9488",
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(n);
}
/** Couleur de médaille selon le rang */
function medalColor(rank: number): string {
  if (rank === 1) return NW.gold;
  if (rank === 2) return NW.silver;
  if (rank === 3) return NW.bronze;
  return NW.dimWhite;
}

interface TopNDimensionSelection {
  dimensionName: string;
  hierarchyName: string;
  levelName: string;
}

function normalizeMetadataName(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

const DATE_SELECTIONS: Record<
  DateGranularity,
  { hierarchyName: string; levelName: string }
> = {
  day: { hierarchyName: "Date", levelName: "Date" },
  week: { hierarchyName: "Week", levelName: "Week" },
  month: { hierarchyName: "Month", levelName: "Month" },
  year: { hierarchyName: "Year", levelName: "Year" },
};

function resolveTopNSelection(
  panel: Panel,
  dimensions: Array<{
    name: string;
    hierarchies: Array<{ name: string; levels: unknown[] }>;
  }>,
): TopNDimensionSelection {
  // Noms réels du cube (nouveaux ou anciens selon la version déployée)
  const ref = resolveSelection(
    { dimensionName: panel.dimensionName, hierarchyName: panel.hierarchyName, levelName: panel.levelName },
    dimensions,
  );
  const dimension = dimensions.find((item) => item.name === ref.dimensionName);
  if (!dimension) {
    throw new Error(`Dimension introuvable dans les métadonnées: ${ref.dimensionName}`);
  }

  const hierarchy = dimension.hierarchies.find(
    (item) =>
      normalizeMetadataName(item.name) ===
      normalizeMetadataName(ref.hierarchyName),
  );
  if (!hierarchy) {
    throw new Error(
      `Hiérarchie introuvable pour ${dimension.name}: ${ref.hierarchyName}`,
    );
  }

  const levelNames = hierarchy.levels.map((level) =>
    typeof level === "string" ? level : (level as { name?: string })?.name,
  );
  if (!levelNames.some((levelName) =>
    normalizeMetadataName(String(levelName)) ===
    normalizeMetadataName(ref.levelName),
  )) {
    throw new Error(
      `Niveau introuvable pour ${dimension.name}.${hierarchy.name}: ${ref.levelName}`,
    );
  }

  return {
    dimensionName: dimension.name,
    hierarchyName: hierarchy.name,
    levelName: ref.levelName,
  };
}

function resolveDateSelection(
  granularity: DateGranularity,
  dimensions: Array<{
    name: string;
    hierarchies: Array<{ name: string; levels: unknown[] }>;
  }>,
): TopNDimensionSelection {
  const dateConfig = DATE_SELECTIONS[granularity];
  const dateDimension = dimensions.find((item) => item.name === "Date");
  if (!dateDimension) {
    throw new Error("Dimension introuvable dans les métadonnées: Date");
  }

  return {
    dimensionName: dateDimension.name,
    hierarchyName: dateConfig.hierarchyName,
    levelName: dateConfig.levelName,
  };
}

/**
 * ✅ Utilise executeQuery → /api/cube/query (même chemin que CouleurView, SousFamilleView)
 * La mesure Pttc est correctement résolue par ce endpoint contrairement à /api/mdx.
 */
async function fetchTopN(
  panel: Panel,
  topN: number,
  dateFrom: string,
  dateTo: string,
  dimensions: Array<{
    name: string;
    hierarchies: Array<{ name: string; levels: unknown[] }>;
  }>,
  depotFilter?: string, // ✅ NOUVEAU : filtre dépôt optionnel
  metric: RankMetric = "ca",
  signal?: AbortSignal,
): Promise<RankRow[]> {
  const selection = resolveTopNSelection(panel, dimensions);
  const depotSelection = resolveTopNSelection(
    PANELS.find((item) => item.id === "magasin")!,
    dimensions,
  );

  const res = await executeQuery({
    cubeName: CUBE_NAME,
    measures: SALES_MEASURES,
    rows: [selection],
    columns: [],
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    // Top N calculé par SSAS (TOPCOUNT) : seules les N lignes reviennent.
    // Le tri + slice ci-dessous restent en filet de sécurité (anciens backends).
    // Marge : des lignes sans libellé / « All » sont filtrées plus bas,
    // il en faut quand même N après filtrage (le slice final remet à N).
    topCount: topN + TOP_N_MARGIN,
    topBy: metric === "qte" ? CUBE_MEASURES.qte : CUBE_MEASURES.caTtc,
    // ✅ NOUVEAU : filtre dépôt si l'utilisateur n'est pas admin
    ...(depotFilter
      ? {
          filters: [
            {
              dimensionName: depotSelection.dimensionName,
              hierarchyName: depotSelection.hierarchyName,
              levelName: depotSelection.levelName,
              members: [depotFilter],
            },
          ],
        }
      : {}),
  }, { signal });

  const { columns, rows: rawData } = res.data as {
    columns: string[];
    rows: any[][];
  };

  // row[0] = membre dimension, puis CA TTC et Qté des ventes
  const { caIdx: pttcIdx, qteIdx } = salesColumnIndexes(columns);

  const rawRows: RankRow[] = (rawData ?? [])
    .filter((row) => {
      const n = String(row[0] ?? "")
        .trim()
        .toLowerCase();
      return (
        n !== "all" &&
        n !== "(all)" &&
        !n.startsWith("all ") &&
        n !== "" &&
        n !== "–"
      );
    })
    .map((row) => {
      const caRaw = pttcIdx >= 0 ? row[pttcIdx] : (row[1] ?? 0);
      const qteRaw = qteIdx >= 0 ? row[qteIdx] : (row[2] ?? 0);
      return {
        member: String(row[0] ?? "–"),
        ca: Math.abs(parseFloat(String(caRaw)) || 0),
        qte: Math.abs(parseFloat(String(qteRaw)) || 0),
      };
    });

  return rawRows
    .sort((a, b) => metricValue(b, metric) - metricValue(a, metric))
    .slice(0, topN);
}

// ─────────────────────────────────────────────────────────────────────────────
// fetchDateSeries : récupère les ventes agrégées selon la granularité choisie
// ─────────────────────────────────────────────────────────────────────────────
interface DateSeriesRow {
  label: string;
  ca: number;
  qte?: number;
}

async function fetchDateSeries(
  granularity: DateGranularity,
  dateFrom: string,
  dateTo: string,
): Promise<DateSeriesRow[]> {
  // Calcul de la plage selon la granularité si aucune date fournie
  const to = dateTo || new Date().toISOString().slice(0, 10);
  let from = dateFrom;
  if (!from) {
    const d = new Date(to);
    if (granularity === "day") d.setDate(d.getDate() - 1);
    else if (granularity === "week") d.setDate(d.getDate() - 7);
    else if (granularity === "month") d.setMonth(d.getMonth() - 1);
    else d.setFullYear(d.getFullYear() - 1);
    from = d.toISOString().slice(0, 10);
  }

  // Resolve the hierarchy through the same metadata path as the ranking panels.
  const dimensions = await getCubeMetadata(CUBE_NAME); // en cache pour la session
  const dateSelection = resolveDateSelection(granularity, dimensions);

  const res = await executeQuery({
    cubeName: CUBE_NAME,
    measures: SALES_MEASURES,
    rows: [dateSelection],
    columns: [], // ✅ champ obligatoire ajouté
    dateFrom: from || undefined,
    dateTo: to || undefined,
  });
  const { columns, rows: rawData } = res.data as {
    columns: string[];
    rows: any[][];
  };

  const { caIdx: pttcIdx, qteIdx } = salesColumnIndexes(columns);

  // Lignes brutes jour par jour
  const daily: DateSeriesRow[] = (rawData ?? [])
    .filter((row) => {
      const n = String(row[0] ?? "")
        .trim()
        .toLowerCase();
      return n !== "" && n !== "all" && n !== "(all)" && !n.startsWith("all ");
    })
    .map((row) => {
      const caRaw = pttcIdx >= 0 ? row[pttcIdx] : (row[1] ?? 0);
      const qteRaw = qteIdx >= 0 ? row[qteIdx] : (row[2] ?? 0);
      return {
        label: String(row[0] ?? "–"),
        ca: Math.abs(parseFloat(String(caRaw)) || 0),
        qte: Math.abs(parseFloat(String(qteRaw)) || 0),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  // The cube has already aggregated the result at the requested level.
  return daily;
}

// ─────────────────────────────────────────────────────────────────────────────
// Composants UI
// ─────────────────────────────────────────────────────────────────────────────

/** Barre de chargement animée */
const Skeleton: React.FC<{ width?: string | number; height?: number }> = ({
  width = "100%",
  height = 16,
}) => (
  <div
    style={{
      width,
      height,
      borderRadius: 6,
      background: `linear-gradient(90deg, ${NW.faint} 25%, rgba(29,91,168,0.12) 50%, ${NW.faint} 75%)`,
      backgroundSize: "200% 100%",
      animation: "nwShimmer 1.4s infinite",
    }}
  />
);

/** Badge de rang (1→🥇, 2→🥈, 3→🥉, n→chiffre) */
const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  const size = 26;
  const isTop3 = rank <= 3;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: isTop3 ? 14 : 10,
        fontWeight: 800,
        background: isTop3
          ? `radial-gradient(circle at 35% 35%, ${medalColor(rank)}CC, ${medalColor(rank)}55)`
          : NW.faint,
        color: isTop3 ? "#FFFFFF" : NW.dimWhite,
        border: `1.5px solid ${isTop3 ? medalColor(rank) + "88" : "transparent"}`,
        boxShadow: isTop3 ? `0 0 8px ${medalColor(rank)}55` : "none",
        flexShrink: 0,
      }}
    >
      {isTop3 ? ["🥇", "🥈", "🥉"][rank - 1] : rank}
    </div>
  );
};

/** Bar horizontale de progression relative */
const ProgressBar: React.FC<{ pct: number; color: string }> = ({
  pct,
  color,
}) => (
  <div
    style={{
      height: 4,
      borderRadius: 99,
      background: NW.faint,
      overflow: "hidden",
      marginTop: 4,
    }}
  >
    <div
      style={{
        height: "100%",
        width: `${Math.max(3, pct)}%`,
        borderRadius: 99,
        background: `linear-gradient(90deg, ${color}99, ${color})`,
        transition: "width 0.6s cubic-bezier(.4,0,.2,1)",
      }}
    />
  </div>
);

// ── Custom Tooltip ────────────────────────────────────────────────────────────
const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.97)",
        border: "1px solid rgba(29,91,168,0.20)",
        borderRadius: 10,
        padding: "8px 14px",
        boxShadow: "0 4px 20px rgba(29,91,168,0.15)",
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 700, color: NW.white, marginBottom: 4 }}>
        {label}
      </div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.color, fontWeight: 600 }}>
          {p.name}: {fmt(p.value)}
        </div>
      ))}
    </div>
  );
};

// ── Chart Type Selector ───────────────────────────────────────────────────────
const ChartTypeSelector: React.FC<{
  value: ChartType;
  onChange: (t: ChartType) => void;
  color: string;
}> = ({ value, onChange, color }) => (
  <div
    style={{
      display: "flex",
      gap: 4,
      background: "rgba(29,91,168,0.06)",
      borderRadius: 10,
      padding: 4,
    }}
  >
    {CHART_TYPES.map(({ type, emoji, label }) => {
      const active = value === type;
      return (
        <button
          key={type}
          onClick={() => onChange(type)}
          title={label}
          style={{
            width: 32,
            height: 32,
            borderRadius: 7,
            border: "none",
            background: active
              ? `linear-gradient(135deg, ${color}, ${color}CC)`
              : "transparent",
            cursor: "pointer",
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.18s",
            boxShadow: active ? `0 2px 10px ${color}55` : "none",
          }}
          onMouseEnter={(e) => {
            if (!active) {
              (e.currentTarget as HTMLElement).style.background = `${color}22`;
            }
          }}
          onMouseLeave={(e) => {
            if (!active) {
              (e.currentTarget as HTMLElement).style.background = "transparent";
            }
          }}
        >
          {emoji}
        </button>
      );
    })}
  </div>
);

// ── Chart Renderer ────────────────────────────────────────────────────────────
const ChartView: React.FC<{
  rows: RankRow[];
  chartType: ChartType;
  color: string;
  metric?: RankMetric;
}> = ({ rows, chartType, color, metric = "ca" }) => {
  // clé tracée dans les graphiques (les deux valeurs restent dans les données)
  const valueKey = metric === "qte" ? "Qte" : "Pttc";
  const valueLabel = METRIC_LABEL[metric];
  const data = rows.map((r) => ({
    name: r.member.length > 10 ? r.member.slice(0, 10) + "…" : r.member,
    fullName: r.member,
    Pttc: r.ca,
    Qte: r.qte ?? 0,
  }));

  const total = rows.reduce((s, r) => s + metricValue(r, metric), 0);

  const commonProps = {
    style: { fontFamily: "Inter, system-ui, -apple-system, sans-serif", fontSize: 11 },
  };

  if (chartType === "bar") {
    return (
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={data}
          margin={{ top: 6, right: 6, left: 0, bottom: 18 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(29,91,168,0.08)" />
          <XAxis
            dataKey="name"
            tick={{
              fontSize: 10,
              fill: NW.dimWhite,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{
              fontSize: 10,
              fill: NW.dimWhite,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) =>
              v >= 1_000_000
                ? `${(v / 1_000_000).toFixed(1)}M`
                : v >= 1000
                  ? `${(v / 1000).toFixed(0)}k`
                  : v
            }
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey={valueKey}
            name={valueLabel} fill={color} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "line") {
    return (
      <ResponsiveContainer width="100%" height={220}>
        <LineChart
          data={data}
          margin={{ top: 6, right: 6, left: 0, bottom: 18 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(29,91,168,0.08)" />
          <XAxis
            dataKey="name"
            tick={{
              fontSize: 10,
              fill: NW.dimWhite,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{
              fontSize: 10,
              fill: NW.dimWhite,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) =>
              v >= 1_000_000
                ? `${(v / 1_000_000).toFixed(1)}M`
                : v >= 1000
                  ? `${(v / 1000).toFixed(0)}k`
                  : v
            }
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey={valueKey}
            name={valueLabel}
            stroke={color}
            strokeWidth={2.5}
            dot={{ fill: color, r: 3, strokeWidth: 0 }}
            activeDot={{ r: 5, fill: color }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "area") {
    return (
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart
          data={data}
          margin={{ top: 6, right: 6, left: 0, bottom: 18 }}
        >
          <defs>
            <linearGradient
              id={`areaGrad-${color.replace("#", "")}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="5%" stopColor={color} stopOpacity={0.25} />
              <stop offset="95%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(29,91,168,0.08)" />
          <XAxis
            dataKey="name"
            tick={{
              fontSize: 10,
              fill: NW.dimWhite,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{
              fontSize: 10,
              fill: NW.dimWhite,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) =>
              v >= 1_000_000
                ? `${(v / 1_000_000).toFixed(1)}M`
                : v >= 1000
                  ? `${(v / 1000).toFixed(0)}k`
                  : v
            }
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey={valueKey}
            name={valueLabel}
            stroke={color}
            strokeWidth={2.5}
            fill={`url(#areaGrad-${color.replace("#", "")})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "pie") {
    const pieData = rows.map((r) => ({
      name: r.member,
      value: metricValue(r, metric),
      pct: total > 0 ? Math.round((metricValue(r, metric) / total) * 100) : 0,
    }));
    return (
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            outerRadius={90}
            dataKey="value"
            label={({ name, percent }) => {
              const n = name ?? "";
              const p = Math.round((percent ?? 0) * 100);
              return `${n.length > 8 ? n.slice(0, 8) + "…" : n} ${p}%`;
            }}
            labelLine={true}
          >
            {pieData.map((_, i) => (
              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name) => [
              fmt(typeof value === "number" ? value : Number(value)),
              name ?? "",
            ]}
            contentStyle={{
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontSize: 11,
              borderRadius: 8,
              border: "1px solid rgba(29,91,168,0.20)",
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "radar") {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <RadarChart data={data} cx="50%" cy="50%" outerRadius={80}>
          <PolarGrid stroke="rgba(29,91,168,0.15)" />
          <PolarAngleAxis
            dataKey="name"
            tick={{
              fontSize: 10,
              fill: NW.dimWhite,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          />
          <PolarRadiusAxis
            tick={{ fontSize: 9, fill: NW.dimWhite }}
            tickFormatter={(v) =>
              v >= 1_000_000
                ? `${(v / 1_000_000).toFixed(0)}M`
                : v >= 1000
                  ? `${(v / 1000).toFixed(0)}k`
                  : v
            }
          />
          <Radar
            dataKey={valueKey}
            name={valueLabel}
            stroke={color}
            fill={color}
            fillOpacity={0.22}
            strokeWidth={2}
          />
          <Tooltip content={<CustomTooltip />} />
        </RadarChart>
      </ResponsiveContainer>
    );
  }

  // Table view
  return (
    <div style={{ overflowX: "auto", marginTop: 4 }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          fontSize: 12,
        }}
      >
        <thead>
          <tr>
            {["#", "Membre", "CA (Pttc)", "Qté", "Part %"].map((h, i) => (
              <th
                key={i}
                style={{
                  padding: "6px 10px",
                  textAlign: i === 0 ? "center" : i >= 2 ? "right" : "left",
                  fontFamily: "Poppins, Inter, system-ui, sans-serif",
                  fontWeight: 700,
                  fontSize: 10.5,
                  color: color,
                  borderBottom: `2px solid ${color}22`,
                  whiteSpace: "nowrap",
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const pct = total > 0 ? ((metricValue(row, metric) / total) * 100).toFixed(1) : "0.0";
            const isEven = idx % 2 === 0;
            return (
              <tr
                key={idx}
                style={{
                  background: isEven ? "transparent" : `${color}06`,
                  transition: "background 0.15s",
                  cursor: "default",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    `${color}12`;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = isEven
                    ? "transparent"
                    : `${color}06`;
                }}
              >
                <td
                  style={{
                    padding: "6px 10px",
                    textAlign: "center",
                    borderBottom: `1px solid ${color}10`,
                  }}
                >
                  <RankBadge rank={idx + 1} />
                </td>
                <td
                  style={{
                    padding: "6px 10px",
                    color: NW.white,
                    fontWeight: 600,
                    borderBottom: `1px solid ${color}10`,
                    maxWidth: 140,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={row.member}
                >
                  {row.member}
                </td>
                <td
                  style={{
                    padding: "6px 10px",
                    textAlign: "right",
                    color: idx === 0 ? color : NW.white,
                    fontWeight: idx === 0 ? 800 : 600,
                    fontFamily: "Poppins, Inter, system-ui, sans-serif",
                    borderBottom: `1px solid ${color}10`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {fmt(row.ca)}
                </td>
                <td
                  style={{
                    padding: "6px 10px",
                    textAlign: "right",
                    color: NW.dimWhite,
                    borderBottom: `1px solid ${color}10`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.qte !== undefined ? fmt(row.qte) : "–"}
                </td>
                <td
                  style={{
                    padding: "6px 10px",
                    textAlign: "right",
                    borderBottom: `1px solid ${color}10`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      justifyContent: "flex-end",
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 4,
                        borderRadius: 99,
                        background: NW.faint,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${Math.max(3, parseFloat(pct))}%`,
                          borderRadius: 99,
                          background: `linear-gradient(90deg, ${color}99, ${color})`,
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: 10.5,
                        color: color,
                        fontWeight: 700,
                        minWidth: 30,
                        textAlign: "right",
                      }}
                    >
                      {pct}%
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td
              colSpan={2}
              style={{
                padding: "6px 10px",
                color: NW.dimWhite,
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              TOTAL
            </td>
            <td
              style={{
                padding: "6px 10px",
                textAlign: "right",
                color: color,
                fontWeight: 800,
                fontFamily: "Poppins, Inter, system-ui, sans-serif",
                fontSize: 12,
              }}
            >
              {fmt(total)}
            </td>
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DatePanel : panneau dédié à l'affichage des ventes par date / semaine / an
// ─────────────────────────────────────────────────────────────────────────────
interface DatePanelProps {
  dateFrom: string;
  dateTo: string;
}

const DatePanel: React.FC<DatePanelProps> = ({ dateFrom, dateTo }) => {
  const COLOR = "#0D9488"; // couleur d'origine du graphique (données)

  // Granularité : défaut = semaine
  const [granularity, setGranularity] = useState<DateGranularity>("week");
  const [rows, setRows] = useState<DateSeriesRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<ChartType>("line");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDateSeries(granularity, dateFrom, dateTo);
      setRows(data);
    } catch (e: any) {
      setError(e?.message ?? "Erreur inconnue");
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [granularity, dateFrom, dateTo]);

  // Recharger à chaque changement de granularité ou de période
  useEffect(() => {
    load();
  }, [load]);

  const hasData = !loading && !error && rows && rows.length > 0;
  const total = rows ? rows.reduce((s, r) => s + r.ca, 0) : 0;

  // Labels des granularités
  const granLabels: Record<DateGranularity, string> = {
    day: "Jour",
    week: "Semaine",
    month: "Mois",
    year: "Année",
  };

  // Données formatées pour Recharts
  const chartData = (rows ?? []).map((r) => ({
    name: r.label.length > 12 ? r.label.slice(0, 12) + "…" : r.label,
    fullName: r.label,
    Pttc: r.ca,
    Qte: r.qte ?? 0,
  }));

  return (
    <div
      style={{
        background: NW.card,
        border: `1px solid ${NW.cardBorder}`,
        borderRadius: 16,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backdropFilter: "blur(12px)",
        boxShadow: `0 2px 16px rgba(0,160,140,0.10)`,
        gridColumn: "1 / -1", // Occupe toute la largeur (2 colonnes)
        transition: "box-shadow 0.2s",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow =
          `0 6px 32px rgba(0,160,140,0.18), 0 0 0 1.5px ${COLOR}44`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow =
          `0 2px 16px rgba(0,160,140,0.10)`;
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 16px 12px",
          borderBottom: `1px solid ${COLOR}18`,
          background: `linear-gradient(135deg, ${COLOR}10, transparent)`,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 20 }}>📅</span>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: "Poppins, Inter, system-ui, sans-serif",
              fontWeight: 700,
              fontSize: 13.5,
              color: COLOR,
              letterSpacing: "-0.2px",
            }}
          >
            Ventes par Date
          </div>
          <div
            style={{
              fontSize: 10,
              color: NW.dimWhite,
              marginTop: 1,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          >
            Évolution CA selon la granularité choisie
          </div>
        </div>

        {/* ── Filtres granularité ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "rgba(0,160,140,0.06)",
            borderRadius: 10,
            padding: "4px 8px",
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: NW.dimWhite,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontWeight: 700,
              marginRight: 4,
            }}
          >
            Afficher par :
          </span>
          {(["day", "week", "month", "year"] as DateGranularity[]).map((g) => {
            const active = granularity === g;
            return (
              <button
                key={g}
                onClick={() => setGranularity(g)}
                style={{
                  padding: "4px 12px",
                  borderRadius: 99,
                  border: `1.5px solid ${active ? COLOR : COLOR + "44"}`,
                  background: active
                    ? `linear-gradient(135deg, ${COLOR}, ${COLOR}CC)`
                    : "transparent",
                  color: active ? "#FFFFFF" : COLOR,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  fontWeight: active ? 800 : 600,
                  fontSize: 11,
                  cursor: "pointer",
                  transition: "all 0.18s",
                  boxShadow: active ? `0 0 12px ${COLOR}44` : "none",
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    (e.currentTarget as HTMLElement).style.background =
                      `${COLOR}18`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    (e.currentTarget as HTMLElement).style.background =
                      "transparent";
                  }
                }}
              >
                {granLabels[g]}
              </button>
            );
          })}
        </div>

        {/* ── Sélecteur type de graphique ── */}
        <ChartTypeSelector
          value={chartType}
          onChange={setChartType}
          color={COLOR}
        />
      </div>

      {/* Body */}
      <div style={{ padding: "10px 16px 14px", flex: 1 }}>
        {/* Loading */}
        {loading && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginTop: 4,
            }}
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <Skeleton width={80} height={13} />
                <div style={{ flex: 1 }}>
                  <Skeleton width="80%" height={13} />
                </div>
                <Skeleton width={70} height={13} />
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              padding: "18px 12px",
              background: "rgba(214,69,69,0.05)",
              borderRadius: 10,
              border: "1px solid rgba(214,69,69,0.15)",
              marginTop: 4,
            }}
          >
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div
              style={{
                color: "#D64545",
                fontSize: 11.5,
                textAlign: "center",
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: 600,
                lineHeight: 1.5,
                wordBreak: "break-word",
              }}
            >
              {error}
            </div>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && rows && rows.length === 0 && (
          <div
            style={{
              color: NW.dimWhite,
              fontSize: 12,
              padding: "20px 0",
              textAlign: "center",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          >
            Aucune donnée pour cette période.
          </div>
        )}

        {/* Chart */}
        {hasData && chartType !== "table" && (
          <div style={{ marginTop: 4 }}>
            {chartType === "bar" && (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={chartData}
                  margin={{ top: 6, right: 6, left: 0, bottom: 28 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,160,140,0.08)"
                  />
                  <XAxis
                    dataKey="name"
                    tick={{
                      fontSize: 10,
                      fill: NW.dimWhite,
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                    }}
                    axisLine={false}
                    tickLine={false}
                    angle={-30}
                    textAnchor="end"
                  />
                  <YAxis
                    tick={{
                      fontSize: 10,
                      fill: NW.dimWhite,
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                    }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) =>
                      v >= 1_000_000
                        ? `${(v / 1_000_000).toFixed(1)}M`
                        : v >= 1000
                          ? `${(v / 1000).toFixed(0)}k`
                          : v
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="Pttc" fill={COLOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
            {chartType === "line" && (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart
                  data={chartData}
                  margin={{ top: 6, right: 6, left: 0, bottom: 28 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,160,140,0.08)"
                  />
                  <XAxis
                    dataKey="name"
                    tick={{
                      fontSize: 10,
                      fill: NW.dimWhite,
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                    }}
                    axisLine={false}
                    tickLine={false}
                    angle={-30}
                    textAnchor="end"
                  />
                  <YAxis
                    tick={{
                      fontSize: 10,
                      fill: NW.dimWhite,
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                    }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) =>
                      v >= 1_000_000
                        ? `${(v / 1_000_000).toFixed(1)}M`
                        : v >= 1000
                          ? `${(v / 1000).toFixed(0)}k`
                          : v
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="Pttc"
                    stroke={COLOR}
                    strokeWidth={2.5}
                    dot={{ fill: COLOR, r: 3, strokeWidth: 0 }}
                    activeDot={{ r: 5, fill: COLOR }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
            {chartType === "area" && (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart
                  data={chartData}
                  margin={{ top: 6, right: 6, left: 0, bottom: 28 }}
                >
                  <defs>
                    <linearGradient
                      id="areaGradDate"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor={COLOR} stopOpacity={0.25} />
                      <stop offset="95%" stopColor={COLOR} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,160,140,0.08)"
                  />
                  <XAxis
                    dataKey="name"
                    tick={{
                      fontSize: 10,
                      fill: NW.dimWhite,
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                    }}
                    axisLine={false}
                    tickLine={false}
                    angle={-30}
                    textAnchor="end"
                  />
                  <YAxis
                    tick={{
                      fontSize: 10,
                      fill: NW.dimWhite,
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                    }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) =>
                      v >= 1_000_000
                        ? `${(v / 1_000_000).toFixed(1)}M`
                        : v >= 1000
                          ? `${(v / 1000).toFixed(0)}k`
                          : v
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="Pttc"
                    stroke={COLOR}
                    strokeWidth={2.5}
                    fill="url(#areaGradDate)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
            {chartType === "pie" && (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={chartData.map((d) => ({
                      name: d.name,
                      value: d.Pttc,
                    }))}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, percent }) => {
                      const p = Math.round((percent ?? 0) * 100);
                      return `${name} ${p}%`;
                    }}
                    labelLine={true}
                  >
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [
                      fmt(typeof value === "number" ? value : Number(value)),
                      name ?? "",
                    ]}
                    contentStyle={{
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                      fontSize: 11,
                      borderRadius: 8,
                      border: "1px solid rgba(0,160,140,0.20)",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
            {chartType === "radar" && (
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={chartData} cx="50%" cy="50%" outerRadius={90}>
                  <PolarGrid stroke="rgba(0,160,140,0.15)" />
                  <PolarAngleAxis
                    dataKey="name"
                    tick={{
                      fontSize: 10,
                      fill: NW.dimWhite,
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                    }}
                  />
                  <PolarRadiusAxis
                    tick={{ fontSize: 9, fill: NW.dimWhite }}
                    tickFormatter={(v) =>
                      v >= 1_000_000
                        ? `${(v / 1_000_000).toFixed(0)}M`
                        : v >= 1000
                          ? `${(v / 1000).toFixed(0)}k`
                          : v
                    }
                  />
                  <Radar
                    name="Pttc"
                    dataKey="Pttc"
                    stroke={COLOR}
                    fill={COLOR}
                    fillOpacity={0.22}
                    strokeWidth={2}
                  />
                  <Tooltip content={<CustomTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            )}
          </div>
        )}

        {/* Table view */}
        {hasData && chartType === "table" && (
          <div style={{ overflowX: "auto", marginTop: 4 }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontSize: 12,
              }}
            >
              <thead>
                <tr>
                  {["#", "Date / Période", "CA (Pttc)", "Qté", "Part %"].map(
                    (h, i) => (
                      <th
                        key={i}
                        style={{
                          padding: "6px 10px",
                          textAlign:
                            i === 0 ? "center" : i >= 2 ? "right" : "left",
                          fontFamily: "Poppins, Inter, system-ui, sans-serif",
                          fontWeight: 700,
                          fontSize: 10.5,
                          color: COLOR,
                          borderBottom: `2px solid ${COLOR}22`,
                          whiteSpace: "nowrap",
                          letterSpacing: 0.3,
                          textTransform: "uppercase",
                        }}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {rows!.map((row, idx) => {
                  const pct =
                    total > 0 ? ((row.ca / total) * 100).toFixed(1) : "0.0";
                  const isEven = idx % 2 === 0;
                  return (
                    <tr
                      key={idx}
                      style={{
                        background: isEven ? "transparent" : `${COLOR}06`,
                        transition: "background 0.15s",
                        cursor: "default",
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.background =
                          `${COLOR}12`;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.background =
                          isEven ? "transparent" : `${COLOR}06`;
                      }}
                    >
                      <td
                        style={{
                          padding: "6px 10px",
                          textAlign: "center",
                          borderBottom: `1px solid ${COLOR}10`,
                          color: NW.dimWhite,
                          fontWeight: 700,
                          fontSize: 11,
                        }}
                      >
                        {idx + 1}
                      </td>
                      <td
                        style={{
                          padding: "6px 10px",
                          color: NW.white,
                          fontWeight: 600,
                          borderBottom: `1px solid ${COLOR}10`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.label}
                      </td>
                      <td
                        style={{
                          padding: "6px 10px",
                          textAlign: "right",
                          color: idx === 0 ? COLOR : NW.white,
                          fontWeight: idx === 0 ? 800 : 600,
                          fontFamily: "Poppins, Inter, system-ui, sans-serif",
                          borderBottom: `1px solid ${COLOR}10`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fmt(row.ca)}
                      </td>
                      <td
                        style={{
                          padding: "6px 10px",
                          textAlign: "right",
                          color: NW.dimWhite,
                          borderBottom: `1px solid ${COLOR}10`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.qte !== undefined ? fmt(row.qte) : "–"}
                      </td>
                      <td
                        style={{
                          padding: "6px 10px",
                          textAlign: "right",
                          borderBottom: `1px solid ${COLOR}10`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            justifyContent: "flex-end",
                          }}
                        >
                          <div
                            style={{
                              width: 40,
                              height: 4,
                              borderRadius: 99,
                              background: NW.faint,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                width: `${Math.max(3, parseFloat(pct))}%`,
                                borderRadius: 99,
                                background: `linear-gradient(90deg, ${COLOR}99, ${COLOR})`,
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: 10.5,
                              color: COLOR,
                              fontWeight: 700,
                              minWidth: 30,
                              textAlign: "right",
                            }}
                          >
                            {pct}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td
                    colSpan={2}
                    style={{
                      padding: "6px 10px",
                      color: NW.dimWhite,
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    TOTAL
                  </td>
                  <td
                    style={{
                      padding: "6px 10px",
                      textAlign: "right",
                      color: COLOR,
                      fontWeight: 800,
                      fontFamily: "Poppins, Inter, system-ui, sans-serif",
                      fontSize: 12,
                    }}
                  >
                    {fmt(total)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Idle */}
        {!loading && !error && rows === null && (
          <div
            style={{
              color: NW.dimWhite,
              fontSize: 12,
              padding: "24px 0",
              textAlign: "center",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          >
            Définissez une période puis cliquez sur{" "}
            <strong style={{ color: COLOR }}>Actualiser</strong>.
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Panneau de classement individuel
// ─────────────────────────────────────────────────────────────────────────────
interface RankingPanelProps {
  panel: Panel;
  rows: RankRow[] | null;
  loading: boolean;
  error: string | null;
  topN: number;
  metric: RankMetric;
}

const RankingPanel: React.FC<RankingPanelProps> = ({
  panel,
  rows,
  loading,
  error,
  topN,
  metric,
}) => {
  const maxCa = rows && rows.length > 0 ? metricValue(rows[0], metric) : 1;
  const [chartType, setChartType] = useState<ChartType>("bar");

  const showChart = chartType !== "table";
  const hasData = !loading && !error && rows && rows.length > 0;

  return (
    <div
      style={{
        background: NW.card,
        border: `1px solid ${NW.cardBorder}`,
        borderRadius: 16,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backdropFilter: "blur(12px)",
        boxShadow: `0 2px 16px rgba(29,91,168,0.10)`,
        transition: "box-shadow 0.2s",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow =
          `0 6px 32px rgba(29,91,168,0.18), 0 0 0 1.5px ${panel.color}44`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow =
          `0 2px 16px rgba(29,91,168,0.10)`;
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 16px 12px",
          borderBottom: `1px solid ${panel.color}18`,
          background: `linear-gradient(135deg, ${panel.color}10, transparent)`,
        }}
      >
        <span style={{ fontSize: 20 }}>{panel.icon}</span>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: "Poppins, Inter, system-ui, sans-serif",
              fontWeight: 700,
              fontSize: 13.5,
              color: colors.brand[700],
              letterSpacing: "-0.2px",
            }}
          >
            {panel.title}
          </div>
          <div
            style={{
              fontSize: 10,
              color: NW.dimWhite,
              marginTop: 1,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          >
            Top {topN} par {metric === "qte" ? "quantité" : "CA"}
          </div>
        </div>
        {/* Chart type selector */}
        <ChartTypeSelector
          value={chartType}
          onChange={setChartType}
          color={panel.color}
        />
      </div>

      {/* Body */}
      <div style={{ padding: "10px 16px 14px", flex: 1 }}>
        {/* Loading */}
        {loading && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginTop: 4,
            }}
          >
            {Array.from({ length: Math.min(topN, 6) }).map((_, i) => (
              <div
                key={i}
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <Skeleton width={26} height={26} />
                <div style={{ flex: 1 }}>
                  <Skeleton width="60%" height={13} />
                  <div style={{ marginTop: 6 }}>
                    <Skeleton width="40%" height={10} />
                  </div>
                </div>
                <Skeleton width={55} height={13} />
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              padding: "18px 12px",
              background: "rgba(214,69,69,0.05)",
              borderRadius: 10,
              border: "1px solid rgba(214,69,69,0.15)",
              marginTop: 4,
            }}
          >
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div
              style={{
                color: "#D64545",
                fontSize: 11.5,
                textAlign: "center",
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: 600,
                lineHeight: 1.5,
                wordBreak: "break-word",
              }}
            >
              {error}
            </div>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && rows && rows.length === 0 && (
          <div
            style={{
              color: NW.dimWhite,
              fontSize: 12,
              padding: "20px 0",
              textAlign: "center",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          >
            Aucune donnée pour cette période.
          </div>
        )}

        {/* Chart or Table */}
        {hasData && chartType !== "table" && (
          <ChartView rows={rows!} chartType={chartType} color={panel.color} metric={metric} />
        )}

        {/* Table */}
        {hasData && chartType === "table" && (
          <ChartView rows={rows!} chartType="table" color={panel.color} metric={metric} />
        )}

        {/* Legacy list view — shown only when bar chart is active AND we want the ranked list below */}
        {/* Removed: the chart replaces the list */}

        {/* Idle (pas encore lancé) */}
        {!loading && !error && rows === null && (
          <div
            style={{
              color: NW.dimWhite,
              fontSize: 12,
              padding: "24px 0",
              textAlign: "center",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          >
            Définissez une période puis cliquez sur{" "}
            <strong style={{ color: NW.mauve }}>Actualiser</strong>.
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ── Export Modals ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

/** Modal 1 : choix du format PDF ou Excel */
const ExportFormatModal: React.FC<{
  onChoose: (format: "pdf" | "excel") => void;
  onClose: () => void;
}> = ({ onChoose, onClose }) => {
  // Fermer sur clic backdrop
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(13,42,80,0.35)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF",
          border: `1.5px solid rgba(29,91,168,0.20)`,
          borderRadius: 18,
          padding: "32px 36px",
          boxShadow: "0 16px 60px rgba(29,91,168,0.18)",
          minWidth: 320,
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>📤</span>
            <span
              style={{
                fontFamily: "Poppins, Inter, system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 16,
                color: "#1D5BA8",
              }}
            >
              Exporter
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(29,91,168,0.08)",
              border: "none",
              borderRadius: 8,
              width: 28,
              height: 28,
              cursor: "pointer",
              fontSize: 14,
              color: "#1D5BA8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        <p
          style={{
            margin: 0,
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            fontSize: 13,
            color: "rgba(24,75,140,0.70)",
          }}
        >
          Choisissez le format d'exportation :
        </p>

        {/* Boutons format */}
        <div style={{ display: "flex", gap: 14 }}>
          {/* PDF */}
          <button
            onClick={() => onChoose("pdf")}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              padding: "18px 12px",
              borderRadius: 14,
              border: "1.5px solid rgba(29,91,168,0.20)",
              background: "rgba(29,91,168,0.04)",
              cursor: "pointer",
              transition: "all 0.18s",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                "rgba(29,91,168,0.10)";
              (e.currentTarget as HTMLElement).style.borderColor = "#1D5BA8";
              (e.currentTarget as HTMLElement).style.transform =
                "translateY(-2px)";
              (e.currentTarget as HTMLElement).style.boxShadow =
                "0 6px 20px rgba(29,91,168,0.18)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                "rgba(29,91,168,0.04)";
              (e.currentTarget as HTMLElement).style.borderColor =
                "rgba(29,91,168,0.20)";
              (e.currentTarget as HTMLElement).style.transform =
                "translateY(0)";
              (e.currentTarget as HTMLElement).style.boxShadow = "none";
            }}
          >
            <span style={{ fontSize: 32 }}>📄</span>
            <span style={{ fontWeight: 800, fontSize: 13, color: "#1D5BA8" }}>
              PDF
            </span>
            <span style={{ fontSize: 11, color: "rgba(24,75,140,0.55)" }}>
              Rapport imprimable
            </span>
          </button>

          {/* Excel */}
          <button
            onClick={() => onChoose("excel")}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              padding: "18px 12px",
              borderRadius: 14,
              border: "1.5px solid rgba(0,160,140,0.25)",
              background: "rgba(0,160,140,0.04)",
              cursor: "pointer",
              transition: "all 0.18s",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                "rgba(0,160,140,0.10)";
              (e.currentTarget as HTMLElement).style.borderColor = "#00A08C";
              (e.currentTarget as HTMLElement).style.transform =
                "translateY(-2px)";
              (e.currentTarget as HTMLElement).style.boxShadow =
                "0 6px 20px rgba(0,160,140,0.18)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                "rgba(0,160,140,0.04)";
              (e.currentTarget as HTMLElement).style.borderColor =
                "rgba(0,160,140,0.25)";
              (e.currentTarget as HTMLElement).style.transform =
                "translateY(0)";
              (e.currentTarget as HTMLElement).style.boxShadow = "none";
            }}
          >
            <span style={{ fontSize: 32 }}>📊</span>
            <span style={{ fontWeight: 800, fontSize: 13, color: "#00A08C" }}>
              Excel
            </span>
            <span style={{ fontSize: 11, color: "rgba(24,75,140,0.55)" }}>
              Données tabulaires
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

/** Modal 2 : saisie du nom de fichier */
const ExportNameModal: React.FC<{
  format: "pdf" | "excel";
  defaultName: string;
  onConfirm: (filename: string) => void;
  onBack: () => void;
  onClose: () => void;
}> = ({ format, defaultName, onConfirm, onBack, onClose }) => {
  const [filename, setFilename] = useState(defaultName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const ext = format === "pdf" ? ".pdf" : ".xlsx";
  const isPdf = format === "pdf";
  const accentColor = isPdf ? "#1D5BA8" : "#00A08C";

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1001,
        background: "rgba(13,42,80,0.40)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF",
          border: `1.5px solid ${accentColor}33`,
          borderRadius: 18,
          padding: "32px 36px",
          boxShadow: `0 16px 60px ${accentColor}22`,
          minWidth: 380,
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>{isPdf ? "📄" : "📊"}</span>
            <span
              style={{
                fontFamily: "Poppins, Inter, system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 16,
                color: accentColor,
              }}
            >
              Exporter en {isPdf ? "PDF" : "Excel"}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: `${accentColor}14`,
              border: "none",
              borderRadius: 8,
              width: 28,
              height: 28,
              cursor: "pointer",
              fontSize: 14,
              color: accentColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        {/* Label */}
        <div>
          <label
            style={{
              display: "block",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontWeight: 700,
              fontSize: 12.5,
              color: accentColor,
              marginBottom: 8,
              letterSpacing: 0.3,
            }}
          >
            Nom du fichier
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
            <input
              ref={inputRef}
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filename.trim())
                  onConfirm(filename.trim());
                if (e.key === "Escape") onClose();
              }}
              placeholder="Nom du fichier"
              style={{
                flex: 1,
                padding: "10px 14px",
                borderRadius: "10px 0 0 10px",
                border: `1.5px solid ${accentColor}44`,
                borderRight: "none",
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontSize: 13,
                color: "#0D2A50",
                outline: "none",
                background: `${accentColor}06`,
                transition: "border-color 0.18s",
              }}
              onFocus={(e) => {
                (e.target as HTMLElement).style.borderColor = accentColor;
              }}
              onBlur={(e) => {
                (e.target as HTMLElement).style.borderColor =
                  `${accentColor}44`;
              }}
            />
            <div
              style={{
                padding: "10px 12px",
                borderRadius: "0 10px 10px 0",
                border: `1.5px solid ${accentColor}44`,
                borderLeft: "none",
                background: `${accentColor}10`,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontSize: 12,
                fontWeight: 700,
                color: accentColor,
                userSelect: "none",
              }}
            >
              {ext}
            </div>
          </div>
          <p
            style={{
              margin: "6px 0 0",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontSize: 11,
              color: "rgba(24,75,140,0.50)",
            }}
          >
            Le fichier sera téléchargé sous le nom :{" "}
            <strong>
              {filename || "…"}
              {ext}
            </strong>
          </p>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onBack}
            style={{
              padding: "9px 18px",
              borderRadius: 10,
              border: "1.5px solid rgba(29,91,168,0.20)",
              background: "transparent",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontWeight: 700,
              fontSize: 13,
              color: "rgba(24,75,140,0.65)",
              cursor: "pointer",
              transition: "all 0.18s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                "rgba(29,91,168,0.06)";
              (e.currentTarget as HTMLElement).style.borderColor = "#1D5BA8";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "transparent";
              (e.currentTarget as HTMLElement).style.borderColor =
                "rgba(29,91,168,0.20)";
            }}
          >
            ← Retour
          </button>
          <button
            onClick={() => filename.trim() && onConfirm(filename.trim())}
            disabled={!filename.trim()}
            style={{
              padding: "9px 22px",
              borderRadius: 10,
              border: "none",
              background: filename.trim()
                ? `linear-gradient(135deg, ${accentColor}, ${accentColor}CC)`
                : "rgba(29,91,168,0.10)",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontWeight: 700,
              fontSize: 13,
              color: filename.trim() ? "#FFFFFF" : "rgba(24,75,140,0.40)",
              cursor: filename.trim() ? "pointer" : "not-allowed",
              boxShadow: filename.trim()
                ? `0 4px 16px ${accentColor}44`
                : "none",
              transition: "all 0.18s",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>⬇</span>
            Télécharger
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Composant principal DashboardPage
// ─────────────────────────────────────────────────────────────────────────────

type PanelState = {
  rows: RankRow[] | null;
  loading: boolean;
  error: string | null;
};

const initState: PanelState = { rows: null, loading: false, error: null };

const DashboardPage: React.FC = () => {
  // ── Filtres ──────────────────────────────────────────────────────────────
  // Helper : retourne la date du jour au format YYYY-MM-DD (requis par input[type="date"])
  const today = (): string => new Date().toISOString().slice(0, 10);

  const oneYearAgo = (): string => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  };
  const [dateFrom, setDateFrom] = useState(oneYearAgo());
  const [dateTo, setDateTo] = useState(today()); // ← pré-rempli avec la date d'aujourd'hui
  const [topN, setTopN] = useState<number>(10);
  const [rankBy, setRankBy] = useState<RankMetric>("ca");

  // ── ✅ NOUVEAU : rôle et dépôt de l'utilisateur connecté ─────────────────
  // La session est garantie vérifiée par l'App avant tout rendu de page
  const { userRole, userDepot, authChecked } = useUserScope();

  const isAdmin = !userRole || userRole === "admin_general" || userRole === "administrateur";
  const isResponsable = userRole === "responsable_boutique" || userRole === "responsable";
  const isVendeur = userRole === "vendeur";

  const visiblePanels = PANELS.filter((p) => {
    if (isAdmin) return true;
    if (isResponsable) return p.id !== "magasin";
    if (isVendeur) return p.id !== "magasin" && p.id !== "vendeur";
    return true;
  });

  // ── Données par panneau ───────────────────────────────────────────────────
  const [panelStates, setPanelStates] = useState<Record<string, PanelState>>(
    Object.fromEntries(PANELS.map((p) => [p.id, { ...initState }])),
  );

  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setPanelStates(
      Object.fromEntries(
        PANELS.map((p) => [p.id, { rows: null, loading: true, error: null }]),
      ),
    );

    // ── ✅ NOUVEAU : filtre dépôt injecté selon le rôle ─────────────────────
    // Pour responsable et vendeur, on passe le dépôt de l'utilisateur
    const depotFilter = !isAdmin && userDepot ? userDepot : undefined;

    let dimensions: Array<{
      name: string;
      hierarchies: Array<{ name: string; levels: unknown[] }>;
    }>;
    try {
      dimensions = await getCubeMetadata(CUBE_NAME); // en cache pour la session
    } catch (error) {
      const message =
        (error as Error)?.message ?? "Impossible de charger les métadonnées du cube";
      setPanelStates(
        Object.fromEntries(
          PANELS.map((p) => [p.id, { rows: null, loading: false, error: message }]),
        ),
      );
      return;
    }

    // Seuls les panneaux visibles pour ce rôle sont interrogés. Les requêtes
    // passent par la file d'executeQuery (quelques-unes à la fois, pour ne pas
    // saturer SSAS) : chaque panneau s'affiche dès que sa réponse arrive.
    const setPanel = (id: string, state: PanelState) => {
      // Un rafraîchissement plus récent a démarré : ce résultat est périmé
      if (controller.signal.aborted) return;
      setPanelStates((prev) => ({ ...prev, [id]: state }));
    };
    await Promise.all(
      visiblePanels.map((p) =>
        fetchTopN(p, topN, dateFrom, dateTo, dimensions, depotFilter, rankBy, controller.signal).then(
          (rows) => setPanel(p.id, { rows, loading: false, error: null }),
          (err) =>
            setPanel(p.id, {
              rows: null,
              loading: false,
              error: (err as Error)?.message ?? "Erreur inconnue",
            }),
        ),
      ),
    );
  }, [dateFrom, dateTo, topN, rankBy, isAdmin, userDepot, visiblePanels]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      refresh(); // chargement initial
      return;
    }
    refresh(); // re-run automatique quand topN ou le critère change
  }, [topN, rankBy]); // eslint-disable-line react-hooks/exhaustive-deps
  const canRefresh = true;
  const TOP_N_OPTIONS = [5, 10, 20, 50];
  const { exportExcel } = useExportExcel();
  const { exportPDF } = useExportPDF();

  // ── Export modals ──────────────────────────────────────────────────────────
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<"pdf" | "excel" | null>(
    null,
  );

  const handleExportChooseFormat = (format: "pdf" | "excel") => {
    setExportFormat(format);
    setShowFormatModal(false);
  };

  const handleExportConfirm = async (filename: string) => {
    if (exportFormat === "excel") {
      await exportExcel({
        filename,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        topN,
        panels: visiblePanels.map((p) => ({
          // ✅ seulement ce que le rôle a le droit de voir
          id: p.id,
          title: p.title,
          icon: p.icon,
          color: p.color.replace("#", ""),
          rows: panelStates[p.id].rows,
        })),
      });
    } else if (exportFormat === "pdf") {
      await exportPDF({
        filename,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        topN,
        panels: visiblePanels.map((p) => ({
          // ✅ seulement ce que le rôle a le droit de voir
          id: p.id,
          title: p.title,
          icon: p.icon,
          color: p.color,
          rows: panelStates[p.id].rows,
        })),
      });
    }
    setExportFormat(null);
  };

  // ✅ déplacé ICI : après TOUS les hooks (useState/useRef/useCallback/useEffect/useExportExcel/useExportPDF)
  if (!authChecked) return null; // ou un spinner

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `radial-gradient(ellipse 80% 40% at 50% 0%, ${NW.bgMid} 0%, #FFFFFF 60%)`,
        padding: "28px 32px 48px",
        boxSizing: "border-box",
      }}
    >
      {/* ── Page title ── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 28 }}>🏆</span>
          <div>
            <h1
              style={{
                margin: 0,
                fontFamily: "Poppins, Inter, system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 22,
                color: NW.mauve,
                letterSpacing: "-0.5px",
              }}
            >
              Top N Dashboard
            </h1>
            <p
              style={{
                margin: 0,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontSize: 12,
                color: NW.dimWhite,
                marginTop: 2,
              }}
            >
              Classements dynamiques par période · 9 dimensions + ventes/date ·
              données SSAS live
            </p>
          </div>
        </div>

        {/* Ligne de séparation */}
        <div
          style={{
            marginTop: 18,
            height: 1,
            background: `linear-gradient(90deg, ${NW.mauve}44, transparent)`,
          }}
        />
      </div>

      {/* ── Barre de filtres ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 18,
          padding: "16px 20px",
          background: NW.card,
          border: `1px solid ${NW.cardBorder}`,
          borderRadius: 14,
          marginBottom: 28,
          backdropFilter: "blur(10px)",
          boxShadow: "0 2px 12px rgba(29,91,168,0.08)",
        }}
      >
        {/* ── Période ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>📅</span>
          <span
            style={{
              fontFamily: "Poppins, Inter, system-ui, sans-serif",
              fontWeight: 700,
              fontSize: 12.5,
              color: NW.mauve,
              letterSpacing: 0.4,
            }}
          >
            Période
          </span>

          <span
            style={{
              fontSize: 11,
              color: NW.dimWhite,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          >
            De :
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={dateInputStyle}
          />

          <span
            style={{
              fontSize: 11,
              color: NW.dimWhite,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          >
            À :
          </span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={dateInputStyle}
          />

          {/* ── Raccourcis période ── */}
          {[
            { label: "7j", days: 7 },
            { label: "1m", days: 30 },
            { label: "1an", days: 365 },
          ].map(({ label, days }) => (
            <button
              key={label}
              onClick={() => {
                const to = new Date();
                const from = new Date();
                from.setDate(from.getDate() - days);
                setDateTo(to.toISOString().slice(0, 10));
                setDateFrom(from.toISOString().slice(0, 10));
              }}
              style={{
                padding: "4px 12px",
                borderRadius: 99,
                border: `1.5px solid rgba(29,91,168,0.30)`,
                background: "rgba(29,91,168,0.06)",
                color: NW.mauve,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: 700,
                fontSize: 12,
                cursor: "pointer",
                transition: "all 0.18s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  "rgba(29,91,168,0.16)";
                (e.currentTarget as HTMLElement).style.borderColor = NW.mauve;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  "rgba(29,91,168,0.06)";
                (e.currentTarget as HTMLElement).style.borderColor =
                  "rgba(29,91,168,0.30)";
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Séparateur vertical */}
        <div
          style={{
            width: 1,
            height: 32,
            background: NW.cardBorder,
            flexShrink: 0,
          }}
        />

        {/* ── Classer par (CA / Quantité) ── */}
        <div
          role="radiogroup"
          aria-label="Classer par"
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <span
            style={{
              fontFamily: "Poppins, Inter, system-ui, sans-serif",
              fontWeight: 700,
              fontSize: 12.5,
              color: NW.mauve,
              letterSpacing: 0.4,
            }}
          >
            Classer par
          </span>
          <div
            style={{
              display: "inline-flex",
              border: `1.5px solid ${NW.cardBorder}`,
              borderRadius: 99,
              padding: 2,
            }}
          >
            {(["ca", "qte"] as RankMetric[]).map((m) => {
              const active = rankBy === m;
              return (
                <button
                  key={m}
                  role="radio"
                  aria-checked={active}
                  onClick={() => setRankBy(m)}
                  style={{
                    padding: "4px 14px",
                    borderRadius: 99,
                    border: "none",
                    background: active ? NW.bgLight : "transparent",
                    color: active ? "#FFFFFF" : NW.dimWhite,
                    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                    fontWeight: active ? 800 : 600,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  {METRIC_LABEL[m]}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Top N ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>🏅</span>
          <span
            style={{
              fontFamily: "Poppins, Inter, system-ui, sans-serif",
              fontWeight: 700,
              fontSize: 12.5,
              color: NW.mauve,
              letterSpacing: 0.4,
              marginRight: 2,
            }}
          >
            Top N
          </span>
          {TOP_N_OPTIONS.map((n) => {
            const active = topN === n;
            return (
              <button
                key={n}
                onClick={() => setTopN(n)}
                style={{
                  padding: "5px 14px",
                  borderRadius: 99,
                  border: `1.5px solid ${active ? NW.mauve : NW.cardBorder}`,
                  background: active
                    ? NW.bgLight
                    : "transparent",
                  color: active ? "#FFFFFF" : NW.dimWhite,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  fontWeight: active ? 800 : 600,
                  fontSize: 12,
                  cursor: "pointer",
                  transition: "all 0.18s",
                  boxShadow: active ? `0 0 14px ${NW.mauve}44` : "none",
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    (e.currentTarget as HTMLElement).style.borderColor =
                      NW.mauve + "88";
                    (e.currentTarget as HTMLElement).style.color = NW.mauve;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    (e.currentTarget as HTMLElement).style.borderColor =
                      NW.cardBorder;
                    (e.currentTarget as HTMLElement).style.color = NW.dimWhite;
                  }
                }}
              >
                Top {n}
              </button>
            );
          })}

          {/* ── Champ N personnalisé ── */}
          <input
            type="number"
            min={1}
            max={9999}
            placeholder="N"
            value={![5, 10, 20, 50].includes(topN) ? topN : ""}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              if (!isNaN(v) && v >= 1) setTopN(v);
            }}
            style={{
              ...dateInputStyle,
              width: 56,
              textAlign: "center",
              fontWeight: 700,
            }}
          />
        </div>

        {/* Séparateur vertical */}
        <div
          style={{
            width: 1,
            height: 32,
            background: NW.cardBorder,
            flexShrink: 0,
          }}
        />

        {/* ── Boutons Actualiser + Export ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginLeft: "auto",
          }}
        >
          {/* Actualiser */}
          <button
            onClick={refresh}
            disabled={!canRefresh}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              padding: "8px 20px",
              borderRadius: 10,
              border: "none",
              background: canRefresh
                ? NW.bgLight
                : NW.faint,
              color: canRefresh ? "#FFFFFF" : NW.dimWhite,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontWeight: 700,
              fontSize: 13,
              cursor: canRefresh ? "pointer" : "not-allowed",
              boxShadow: canRefresh ? `0 0 18px ${NW.mauve}33` : "none",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (canRefresh) {
                (e.currentTarget as HTMLElement).style.boxShadow =
                  `0 0 28px ${NW.mauve}55`;
                (e.currentTarget as HTMLElement).style.transform =
                  "translateY(-1px)";
              }
            }}
            onMouseLeave={(e) => {
              if (canRefresh) {
                (e.currentTarget as HTMLElement).style.boxShadow =
                  `0 0 18px ${NW.mauve}33`;
                (e.currentTarget as HTMLElement).style.transform =
                  "translateY(0)";
              }
            }}
          >
            <span>⟳</span>
            Actualiser
          </button>

          {/* Export */}
          <button
            onClick={() => setShowFormatModal(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              padding: "8px 20px",
              borderRadius: 10,
              border: `1.5px solid rgba(29,91,168,0.30)`,
              background: "rgba(29,91,168,0.06)",
              color: NW.mauve,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                "rgba(29,91,168,0.12)";
              (e.currentTarget as HTMLElement).style.borderColor = NW.mauve;
              (e.currentTarget as HTMLElement).style.boxShadow =
                `0 0 18px ${NW.mauve}22`;
              (e.currentTarget as HTMLElement).style.transform =
                "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                "rgba(29,91,168,0.06)";
              (e.currentTarget as HTMLElement).style.borderColor =
                "rgba(29,91,168,0.30)";
              (e.currentTarget as HTMLElement).style.boxShadow = "none";
              (e.currentTarget as HTMLElement).style.transform =
                "translateY(0)";
            }}
          >
            <span>📤</span>
            Exporter
          </button>
        </div>
      </div>

      {/* ── Modals Export ── */}
      {showFormatModal && (
        <ExportFormatModal
          onChoose={handleExportChooseFormat}
          onClose={() => setShowFormatModal(false)}
        />
      )}
      {exportFormat && (
        <ExportNameModal
          format={exportFormat}
          defaultName={`Top_N_Dashboard_${new Date().toISOString().slice(0, 10)}`}
          onConfirm={handleExportConfirm}
          onBack={() => {
            setExportFormat(null);
            setShowFormatModal(true);
          }}
          onClose={() => setExportFormat(null)}
        />
      )}

      {/* ── Grille de panneaux — 2 par ligne ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 20,
        }}
      >
        {/* ✅ APRÈS : visiblePanels au lieu de PANELS */}
        {visiblePanels.map((panel) => (
          <RankingPanel
            key={panel.id}
            panel={panel}
            topN={topN}
            metric={rankBy}
            rows={panelStates[panel.id].rows}
            loading={panelStates[panel.id].loading}
            error={panelStates[panel.id].error}
          />
        ))}

        {/* ── Panel Date — pleine largeur, avec filtre 1j/1sem/1an ── */}
        <DatePanel dateFrom={dateFrom} dateTo={dateTo} />
      </div>

      {/* ── Footer info ── */}
      <div
        style={{
          marginTop: 36,
          textAlign: "center",
          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          fontSize: 11,
          color: "rgba(29,91,168,0.40)",
          letterSpacing: 0.5,
        }}
      >
        Cube :{" "}
        <strong style={{ color: "rgba(29,91,168,0.65)" }}>{CUBE_NAME}</strong>
        &nbsp;·&nbsp;Mesure principale :{" "}
        <strong style={{ color: "rgba(29,91,168,0.65)" }}>{MEASURE_CA}</strong>
        &nbsp;·&nbsp;Sans filtre de période = toutes dates · 9 classements +
        ventes/date
      </div>

      {/* ── Styles globaux ── */}
      <style>{`
        @keyframes nwShimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(29,91,168,0.25); border-radius: 99px; }
        input[type="date"] { color-scheme: light; }
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(0.3) sepia(0.5) hue-rotate(250deg);
          cursor: pointer;
          opacity: 0.7;
        }
        input[type="date"]::-webkit-calendar-picker-indicator:hover { opacity: 1; }
      `}</style>
    </div>
  );
};

// ── Style commun pour les inputs date ────────────────────────────────────────
const dateInputStyle: React.CSSProperties = {
  padding: "5px 10px",
  borderRadius: 8,
  border: `1px solid rgba(29,91,168,0.30)`,
  background: "rgba(29,91,168,0.04)",
  color: "#0D2A50",
  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
  fontSize: 12,
  outline: "none",
  cursor: "pointer",
  transition: "border-color 0.18s",
};

export { DashboardPage as DashboardView };
export default DashboardPage;
